import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

//interface SearchPayload {
//    startDate: string | null;
//    endDate: string | null;
//}

type SearchPayload = { startDate: string | null; endDate: string | null };
type SearchPayloadChangedArg =
    | Partial<SearchPayload>
    | { searchPayload: Partial<SearchPayload> };

export interface PdSearchState {
    mode: string;
    searchPayload: SearchPayload;
    selectedDeliveryFormats: any[];
    selectedPassportCredits: any[];
    selectedCoursePrograms: any[];
    selectedCPDHours: any[];
    selectedEthicsHours: any[];
    selectedCertificatePrograms: any[];
    selectedCourseSubjects: any[];
    selectedLocations: any[];
    selectedLeaders: any[];
    boolNewCourses: boolean;
    boolCancelledCourses: boolean;
    boolPastCourses: boolean;
    boolCPAPro: boolean;
    boolBookmarked: boolean;
    sortBy: string;
    pageIndex: number;
    loading: boolean;
    errorStatus: number | null;
    courses: any[];
    totalCountCourses: number;
    selectedBookmarks: any[];
    courseDetailsItem: any | null;
    courseDetails: any | null;
    loadingCourseDetails: boolean;

    searchPayloadCalendar?: {
        StartDate: string | null;
        EndDate: string | null;
    };

    keywordsSearchValue?: string;
    allSearchCriteria?: any | null;
    courseFromHashVisible?: boolean;
    hashAllSearchCriteria?: string;
    newCoursesOnlyVisible?: boolean;
    copySearchTagVisible?: boolean;
    searchTag?: string;
    listBookmarksVisible?: boolean;
    isLoadingPdDetails?: boolean;
    selectedSeminarLength?: any[];

    coursesExcel: any[];
    loadingExcel: boolean;
    errorStatusExcel: number | null;
}

const initialState: PdSearchState = {
    mode: "list",
    searchPayload: {
        startDate: null,
        endDate: null,
    },
    selectedDeliveryFormats: [],
    selectedPassportCredits: [],
    selectedCoursePrograms: [],
    selectedCPDHours: [],
    selectedEthicsHours: [],
    selectedCertificatePrograms: [],
    selectedCourseSubjects: [],
    selectedLocations: [],
    selectedLeaders: [],
    boolNewCourses: false,
    boolCancelledCourses: false,
    boolPastCourses: false,
    boolCPAPro: false,
    boolBookmarked: false,
    sortBy: "Date",
    pageIndex: 0,
    loading: false,
    errorStatus: null,
    courses: [],
    totalCountCourses: 0,
    selectedBookmarks: [],
    courseDetailsItem: null,
    courseDetails: null,
    loadingCourseDetails: false,

    searchPayloadCalendar: {
        StartDate: null,
        EndDate: null,
    },
    keywordsSearchValue: "",
    allSearchCriteria: null,

    courseFromHashVisible: false,
    hashAllSearchCriteria: "",

    newCoursesOnlyVisible: false,
    copySearchTagVisible: false,
    searchTag: "",

    listBookmarksVisible: false,
    isLoadingPdDetails: true,

    selectedSeminarLength: [],

    coursesExcel: [],
    loadingExcel: false,
    errorStatusExcel: null,
};

const extractCourses = (apiData: any): any[] => {
    const payload = apiData?.data ?? apiData;
    if (Array.isArray(payload)) return payload;

    return (
        payload?.courses ??
        payload?.Courses ??
        payload?.items ??
        payload?.Items ??
        payload?.results ??
        payload?.Results ??
        []
    );
};

export const getListCoursesExcel = createAsyncThunk<
    any[],
    { searchRequest: any; totalCountCourses: number },
    { rejectValue: number }
>("courses/getListCoursesExcel", async ({ searchRequest, totalCountCourses }, thunkApi) => {
    try {
        const pageSize = 50;
        const target = Number(totalCountCourses) || 0;

        const allCourses: any[] = [];
        let pageIndex = 0;

        while (allCourses.length < target || (target === 0 && pageIndex === 0)) {
            const req = {
                ...searchRequest,
                PageIndex: pageIndex,
                PageSize: pageSize,
            };

            const response = await axios.post("/api/pdsearch", req, { signal: thunkApi.signal });
            const data = response.data;

            const pageCourses = extractCourses(data);
            allCourses.push(...pageCourses);

            if (!pageCourses || pageCourses.length === 0 || pageCourses.length < pageSize) break;

            pageIndex += 1;

            if (pageIndex > 500) break;
        }

        return allCourses;
    } catch (error: any) {
        return thunkApi.rejectWithValue(error?.response?.status);
    }
});

export const getListCourses = createAsyncThunk<any, any>(
    "courses/getListCourses",
    async (searchRequest, thunkApi) => {
        try {
            const response = await axios.post("/api/pdsearch", searchRequest);
            const data = await response.data;
            return thunkApi.fulfillWithValue(data);
        } catch (error: any) {
            const err = error;
            return thunkApi.rejectWithValue(err.response?.status);
        }
    }
);

export const getCourseDetails = createAsyncThunk<any, any>(
    "courses/getCourseDetails",
    async (arg, thunkApi) => {
        try {
            let masterCode: string = "";
            let searchRequest: any = null;

            if (typeof arg === "string") {
                masterCode = arg;
            } else if (arg && typeof arg === "object") {
                masterCode = arg.masterCode;
                searchRequest = arg.searchRequest ?? null;
            }

            const url = "/api/pdsearch/" + (masterCode ? masterCode : "");

            const safeBody = searchRequest ?? {};

            const response = await axios.post(url, safeBody, {
                headers: { "Content-Type": "application/json" },
            });

            const data = response.data;

            if (data.currentCourse) {
                try {
                    (window as any).dataLayer = (window as any).dataLayer || [];
                    const eventToPush = {
                        event: "page_view",
                        page_location: `${window.location.href}`,
                        page_title: `Seminar - ${data.currentCourse.title}`,
                    };

                    (window as any).dataLayer.push(eventToPush);
                } catch {
                    // ignored
                }
            }

            return data;
        } catch (error: any) {
            const err = error;
            return thunkApi.rejectWithValue(err.response?.status);
        }
    }
);

interface GetCourseDetailsArgs {
    masterCode: string;
    searchRequest?: any | null;
}

type GetCourseDetailsUrlArg = GetCourseDetailsArgs | string;

export const getCourseDetailsUrl = createAsyncThunk<any, GetCourseDetailsUrlArg>(
    "courses/getCourseDetailsUrl",
    async (arg, thunkApi) => {
        try {
            const masterCode = typeof arg === "string" ? arg : arg.masterCode;
            const searchRequest = typeof arg === "string" ? null : (arg.searchRequest ?? null);
            const url = "/api/pdsearch/" + (masterCode ? masterCode : "");

            const response = await axios.post(url, searchRequest ?? {}, {
                signal: thunkApi.signal,
            });

            const data = await response.data;

            if (data.currentCourse) {
                try {
                    (window as any).dataLayer = (window as any).dataLayer || [];
                    const eventToPush = {
                        event: "page_view",
                        page_location: `${window.location.href}`,
                        page_title: `Seminar - ${data.currentCourse.title}`,
                    };

                    (window as any).dataLayer.push(eventToPush);
                } catch {
                    // ignored
                }
            }

            return data;
        } catch (error: any) {
            const err = error;
            return thunkApi.rejectWithValue(err.response?.status);
        }
    }
);



export const pdSearchSlice = createSlice({
    name: "pdSearch",
    initialState,
    reducers: {
        setMode: (state, action) => {
            state.mode = action.payload;
        },
        searchPayloadChanged: (state, action) => {
            const next =
                "searchPayload" in action.payload
                    ? action.payload.searchPayload
                    : action.payload;

            state.searchPayload = { ...state.searchPayload, ...next };
        },
        addDeliveryFormatsItem: (state, action) => {
            state.selectedDeliveryFormats = [...state.selectedDeliveryFormats, action.payload];
        },
        removeDeliveryFormatsItem: (state, action) => {
            state.selectedDeliveryFormats = state.selectedDeliveryFormats.filter(
                (item: any) => item.code !== action.payload.code
            );
        },
        setDeliveryFormatsItems: (state, action) => {
            state.selectedDeliveryFormats = action.payload;
        },
        addPassportCreditsItem: (state, action) => {
            state.selectedPassportCredits = [...state.selectedPassportCredits, action.payload];
        },
        removePassportCreditsItem: (state, action) => {
            state.selectedPassportCredits = state.selectedPassportCredits.filter(
                (item: any) => item.value !== action.payload.value
            );
        },
        setPassportCreditsItems: (state, action) => {
            state.selectedPassportCredits = action.payload;
        },
        addCourseProgramsItem: (state, action) => {
            state.selectedCoursePrograms = [...state.selectedCoursePrograms, action.payload];
        },
        removeCourseProgramsItem: (state, action) => {
            state.selectedCoursePrograms = state.selectedCoursePrograms.filter(
                (item: any) => item.description !== action.payload.description
            );
        },
        setCourseProgramsItems: (state, action) => {
            state.selectedCoursePrograms = action.payload;
        },
        addCPDHoursItem: (state, action) => {
            state.selectedCPDHours = [...state.selectedCPDHours, action.payload];
        },
        removeCPDHoursItem: (state, action) => {
            state.selectedCPDHours = state.selectedCPDHours.filter(
                (item: any) => item.text !== action.payload.text
            );
        },
        setCPDHoursItems: (state, action) => {
            state.selectedCPDHours = action.payload;
        },
        addEthicsHoursItem: (state, action) => {
            state.selectedEthicsHours = [...state.selectedEthicsHours, action.payload];
        },
        removeEthicsHoursItem: (state, action) => {
            state.selectedEthicsHours = state.selectedEthicsHours.filter(
                (item: any) => item.text !== action.payload.text
            );
        },
        setEthicsHoursItems: (state, action) => {
            state.selectedEthicsHours = action.payload;
        },
        addCertificateProgramsItem: (state, action) => {
            state.selectedCertificatePrograms = [...state.selectedCertificatePrograms, action.payload];
        },
        removeCertificateProgramsItem: (state, action) => {
            state.selectedCertificatePrograms = state.selectedCertificatePrograms.filter(
                (item: any) => item.description !== action.payload.description
            );
        },
        setCertificateProgramsItems: (state, action) => {
            state.selectedCertificatePrograms = action.payload;
        },
        addCourseSubjectsItem: (state, action) => {
            state.selectedCourseSubjects = [...state.selectedCourseSubjects, action.payload];
        },
        removeCourseSubjectsItem: (state, action) => {
            state.selectedCourseSubjects = state.selectedCourseSubjects.filter(
                (item: any) => item.description !== action.payload.description
            );
        },
        setCourseSubjectsItems: (state, action) => {
            state.selectedCourseSubjects = action.payload;
        },
        addLocationsItem: (state, action) => {
            state.selectedLocations = [...state.selectedLocations, action.payload];
        },
        removeLocationsItem: (state, action) => {
            state.selectedLocations = state.selectedLocations.filter(
                (item: any) => item.city !== action.payload.city
            );
        },
        setSelectedLocations: (state, action) => {
            state.selectedLocations = action.payload;
        },
        addLeadersItem: (state, action) => {
            state.selectedLeaders = [...state.selectedLeaders, action.payload];
        },
        removeLeadersItem: (state, action) => {
            state.selectedLeaders = state.selectedLeaders.filter(
                (item: any) => item.leaderId !== action.payload.leaderId
            );
        },
        setLeadersItems: (state, action) => {
            state.selectedLeaders = action.payload;
        },
        setBoolNewCourses: (state, action) => {
            state.boolNewCourses = action.payload;
        },
        setBoolCancelledCourses: (state, action) => {
            state.boolCancelledCourses = action.payload;
        },
        setBoolPastCourses: (state, action) => {
            state.boolPastCourses = action.payload;
        },
        setBoolCPAPro: (state, action) => {
            state.boolCPAPro = action.payload;
        },
        setBoolBookmarked: (state, action) => {
            state.boolBookmarked = action.payload;
        },
        setSortByValue: (state, action) => {
            state.sortBy = action.payload;
        },
        setSelectedBookmarks: (state, action) => {
            state.selectedBookmarks = action.payload;
        },
        setPageIndex: (state, action) => {
            state.pageIndex = action.payload;
        },
        setEmptyCourses: (state) => {
            state.courses = [];
        },
        clearFilters: (state) => {
            state.selectedDeliveryFormats = [];
            state.selectedPassportCredits = [];
            state.selectedCoursePrograms = [];
            state.selectedCPDHours = [];
            state.selectedEthicsHours = [];
            state.selectedCertificatePrograms = [];
            state.selectedCourseSubjects = [];
            state.selectedLocations = [];
            state.selectedLeaders = [];
            state.boolNewCourses = false;
            state.boolCancelledCourses = false;
            state.boolPastCourses = false;
            state.boolCPAPro = false;
            state.boolBookmarked = false;
        },
        setCourseDetailsItem: (state, action) => {
            state.courseDetailsItem = action.payload;
        },
        setEmptyCourseDetails: (state) => {
            state.courseDetails = null;
        },


        searchPayloadCalendarChanged: (state, action) => {
            state.searchPayloadCalendar = {
                ...(state.searchPayloadCalendar ?? { StartDate: null, EndDate: null }),
                ...(action.payload ?? {}),
            };
        },

        setKeywordsSearchValue: (state, action) => {
            state.keywordsSearchValue = action.payload;
        },
        setAllSearchCriteria: (state, action) => {
            state.allSearchCriteria = action.payload;
        },

        setCourseFromHashVisible: (state, action) => {
            state.courseFromHashVisible = action.payload;
        },
        setHashAllSearchCriteria: (state, action) => {
            state.hashAllSearchCriteria = action.payload;
        },

        setNewCoursesOnlyVisible: (state, action) => {
            state.newCoursesOnlyVisible = action.payload;
        },
        setCopySearchTagVisible: (state, action) => {
            state.copySearchTagVisible = action.payload;
        },
        setSearchTag: (state, action) => {
            state.searchTag = action.payload;
        },

        setVisibleListBookmarks: (state, action) => {
            state.listBookmarksVisible = action.payload;
        },

        setIsLoadingPdDetails: (state, action) => {
            state.isLoadingPdDetails = action.payload;
        },

        setSelectedSeminarLengthItems: (state, action) => {
            state.selectedSeminarLength = action.payload;
        },

        selectBookmark: (state, action) => {
            state.selectedBookmarks = action.payload;
        },
        setLoadingFalse: (state, action) => {
            state.loading = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getListCourses.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getListCourses.fulfilled, (state, action) => {
            state.courses = action.payload.items
                ? state.courses.concat(action.payload.items)
                : state.courses;
            state.totalCountCourses = action.payload.totalCount;
            state.loading = false;
        });
        builder.addCase(getListCourses.rejected, (state, action) => {
            state.errorStatus = action.payload as number | null;
        });
        builder.addCase(getCourseDetails.pending, (state) => {
            state.loadingCourseDetails = true;
        });
        builder.addCase(getCourseDetails.fulfilled, (state, action) => {
            state.courseDetails = action.payload;
            state.loadingCourseDetails = false;
        });
        builder.addCase(getCourseDetails.rejected, (state, action) => {
            state.errorStatus = action.payload as number | null;
        });
        builder.addCase(getCourseDetailsUrl.pending, (state) => {
            state.loadingCourseDetails = true;
        });
        builder.addCase(getCourseDetailsUrl.fulfilled, (state, action) => {
            state.courseDetailsItem = action.payload.currentCourse
                ? action.payload.currentCourse
                : [];
            state.courseDetails = action.payload;
            state.loadingCourseDetails = false;
        });
        builder.addCase(getCourseDetailsUrl.rejected, (state, action) => {
            state.errorStatus = action.payload as number | null;
        });
        builder
            .addCase(getListCoursesExcel.pending, (state) => {
                state.loadingExcel = true;
                state.errorStatusExcel = null;
                state.coursesExcel = [];
            })
            .addCase(getListCoursesExcel.fulfilled, (state, action) => {
                state.loadingExcel = false;
                state.coursesExcel = action.payload ?? [];
            })
            .addCase(getListCoursesExcel.rejected, (state, action) => {
                state.loadingExcel = false;
                state.errorStatusExcel = (action.payload as number) ?? null;
                state.coursesExcel = [];
            });
    },
});

export const {
    searchPayloadChanged,
    addDeliveryFormatsItem,
    removeDeliveryFormatsItem,
    addPassportCreditsItem,
    removePassportCreditsItem,
    addCourseProgramsItem,
    removeCourseProgramsItem,
    addEthicsHoursItem,
    removeEthicsHoursItem,
    addCertificateProgramsItem,
    removeCertificateProgramsItem,
    addCourseSubjectsItem,
    removeCourseSubjectsItem,
    addLocationsItem,
    removeLocationsItem,
    addLeadersItem,
    removeLeadersItem,
    setBoolNewCourses,
    setBoolCancelledCourses,
    setBoolPastCourses,
    setBoolCPAPro,
    setBoolBookmarked,
    setSortByValue,
    setCourseSubjectsItems,
    setPassportCreditsItems,
    setDeliveryFormatsItems,
    setCourseProgramsItems,
    setEthicsHoursItems,
    setCertificateProgramsItems,
    setLeadersItems,
    setSelectedBookmarks,
    setMode,
    clearFilters,
    setPageIndex,
    setEmptyCourses,
    setCourseDetailsItem,
    setEmptyCourseDetails,
    setSelectedLocations,
    addCPDHoursItem,
    removeCPDHoursItem,
    setCPDHoursItems,

    searchPayloadCalendarChanged,
    setKeywordsSearchValue,
    setAllSearchCriteria,
    setCourseFromHashVisible,
    setHashAllSearchCriteria,
    setNewCoursesOnlyVisible,
    setCopySearchTagVisible,
    setSearchTag,
    setVisibleListBookmarks,
    setIsLoadingPdDetails,
    setSelectedSeminarLengthItems,
    selectBookmark,
    setLoadingFalse
} = pdSearchSlice.actions;

export default pdSearchSlice.reducer;

